import React from "react";
import { C } from "../theme";
import { APP_VERSION } from "../appVersion";

export const SUPPORT_EMAIL = "support@the99-app.com";

// Crisp vector envelope so the icon stays sharp at any DPI (the previous
// "✉" Unicode glyph rendered as a low-res color-emoji bitmap that looked
// fuzzy when scaled inside the support pill).
export function MailIcon({ size = 14, color = "currentColor" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}

/**
 * Build a `mailto:` URL to support, pre-filled with a subject and a body that
 * captures app/device context so the support team has what they need to help.
 *
 * @param {object} opts
 * @param {string} [opts.context] - Short label for where the user is / what
 *   they were doing (e.g. "Trip Report", "Catch Screen").
 * @param {string} [opts.error] - Optional error message to include.
 * @param {string} [opts.userId] - Optional user id for account lookup.
 */
export function buildSupportMailto({ context = "", error = "", userId = "", sessionId = "" } = {}) {
  const subject = `THE 99 Support${context ? ` — ${context}` : ""}`;
  const divider = "──────────────────────────────";
  const lines = [
    "Hi THE 99 Support team,",
    "",
    "I ran into a problem and could use a hand:",
    "",
    "  ▸ What happened: ",
    "  ▸ What I expected: ",
    "",
    divider,
    "Diagnostics (auto-filled — please leave this in)",
    divider,
    `App version:  ${APP_VERSION}`,
  ];
  if (context) lines.push(`Screen:       ${context}`);
  if (sessionId) lines.push(`Session ID:   ${sessionId}`);
  if (userId) lines.push(`User ID:      ${userId}`);
  if (error) lines.push(`Error:        ${error}`);
  if (typeof navigator !== "undefined" && navigator.userAgent) {
    lines.push(`Device:       ${navigator.userAgent}`);
  }
  lines.push(divider);
  const body = lines.join("\n");
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * Reusable "contact support" affordance. Two variants:
 *   - variant="floating": small circular help button (for the app frame).
 *   - variant="inline":   pill button with a label (for error states).
 */
export default function SupportButton({
  variant = "inline",
  context = "",
  error = "",
  userId = "",
  sessionId = "",
  label = "Contact support",
  style = {},
}) {
  const href = buildSupportMailto({ context, error, userId, sessionId });

  if (variant === "floating") {
    return (
      <a
        href={href}
        title="Contact support"
        aria-label="Contact support"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          borderRadius: 999,
          padding: "7px 13px",
          border: `1px solid ${C.seafoam}66`,
          background: C.cardBg,
          color: C.white,
          cursor: "pointer",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.3,
          textDecoration: "none",
          boxShadow: "0 2px 10px rgba(0,0,0,0.35)",
          ...style,
        }}
      >
        <span aria-hidden style={{ display: "inline-flex", lineHeight: 0 }}>
          <MailIcon size={14} />
        </span>
        Support
      </a>
    );
  }

  return (
    <a
      href={href}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        borderRadius: 999,
        padding: "9px 16px",
        background: "transparent",
        color: C.white,
        fontWeight: 700,
        fontSize: 13,
        cursor: "pointer",
        border: `1px solid ${C.border}`,
        textDecoration: "none",
        ...style,
      }}
    >
      <span aria-hidden style={{ display: "inline-flex", lineHeight: 0 }}>
        <MailIcon size={14} />
      </span>
      {label}
    </a>
  );
}
